/**
 * Global route middleware that manages A/B experiment lifecycle.
 * Runs on every navigation. Numbered `03` to execute after auth middleware.
 *
 * **Responsibilities:**
 *
 * 1. **Cleanup**: removes cookies for experiments no longer in the active
 *    `core.experiments` config. Runs on both SSR and client so a stale
 *    cookie is wiped from the response before the client sees it —
 *    otherwise `useExperiment`'s readonly path would return the stale
 *    variant on SSR and `'a'` on the client, causing a hydration mismatch.
 *
 * 2. **Assignment**: for each active experiment with versions A and B
 *    defined, calls `useExperiment({ allowWrite: true })`. Selection lives in
 *    the composable; don't pre-write the cookie here.
 *
 * @see CoreExperiment for experiment configuration shape
 * @see useExperiment for the assignment composable
 */
import { ExperimentCookieName, ExperimentValue, type CoreExperiment } from '@core/types'

export default defineNuxtRouteMiddleware((to) => {
    const { experiments } = useCoreConfig()

    const cleanupInactiveExperiments = () => {
        const activeExperimentSet = new Set(
            experiments?.map(experiment => experiment.cookieName) ?? [],
        )
        const possibleExperiments = Object.values(ExperimentCookieName)

        possibleExperiments.forEach((cookieName) => {
            // Don't remove active experiments
            if (activeExperimentSet.has(cookieName)) return

            const experimentCookie = useCookie(cookieName)

            // If the cookie exists, clear it
            if (experimentCookie.value) {
                experimentCookie.value = null
            }
        })
    }

    cleanupInactiveExperiments()

    if (!experiments?.length) return

    // Set cookie for each active experiment
    experiments.forEach((experiment: CoreExperiment) => {
        // Prevent setting cookie for empty required experiments
        if (!experiment?.versions?.[ExperimentValue.A] || !experiment?.versions?.[ExperimentValue.B]) return

        useExperiment({
            cookieName: experiment.cookieName,
            experimentName: experiment.name,
            allowWrite: true,
            override: experimentOverride(to.query, experiment.name),
        })
    })
})
